/**
 * JWT utility functions for token management
 */

interface JWTPayload {
  exp?: number;
  [key: string]: unknown;
}

/**
 * Decodes a JWT token without verifying the signature
 * This is safe for checking expiration on the client side
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Checks if a JWT token is expired
 * Returns true if expired, false if valid, null if unable to determine
 */
export function isTokenExpired(token: string): boolean | null {
  const payload = decodeJWT(token);
  if (!payload || typeof payload.exp !== 'number') {
    return false;
  }

  // JWT exp is in seconds, Date.now() is in milliseconds
  const now = Math.floor(Date.now() / 1000);
  // Add 30 second buffer to account for clock skew and network latency
  return payload.exp < now + 30;
}
